import React, { useState, useMemo, useEffect } from 'react';
import {
  Phone, PhoneCall, PhoneMissed, PhoneOff, Clock,
  MessageCircle, CheckCircle, XCircle, CalendarClock,
  CalendarPlus, X, Loader, FileText, DollarSign, AlertCircle, ChevronDown, Trash2
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getWhatsappLink, formatDate } from '../../utils/helpers';
import { generateCallCalendarUrl, openInGoogleCalendar } from '../../utils/googleCalendar';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { STAGES, VENUES, EVENT_TYPES, LOSS_REASONS } from '../../lib/constants';
import { bookingService } from '../../services/booking.service';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker, CustomCheckbox, Toast } from '../ui';

const CALL_OUTCOMES = [
  { id: 'connected_interested', label: 'Connected - Interested', labelUr: 'رابطہ ہوا - دلچسپی ہے', icon: CheckCircle, color: 'emerald' },
  { id: 'connected_not_interested', label: 'Connected - Not Interested', labelUr: 'رابطہ ہوا - دلچسپی نہیں', icon: XCircle, color: 'red' },
  { id: 'connected_callback', label: 'Connected - Callback Requested', labelUr: 'رابطہ ہوا - دوبارہ کال کریں', icon: PhoneCall, color: 'blue' },
  { id: 'no_answer', label: 'No Answer', labelUr: 'جواب نہیں', icon: PhoneMissed, color: 'amber' },
  { id: 'busy', label: 'Busy / Call Later', labelUr: 'مصروف / بعد میں کال کریں', icon: Clock, color: 'orange' },
  { id: 'wrong_number', label: 'Wrong Number', labelUr: 'غلط نمبر', icon: PhoneOff, color: 'gray' }
];

const OUTCOME_STYLES = {
  emerald: { btn: 'border-emerald-400 bg-emerald-50 text-emerald-700', icon: 'text-emerald-500', dot: 'bg-emerald-400' },
  red: { btn: 'border-red-400 bg-red-50 text-red-700', icon: 'text-red-500', dot: 'bg-red-400' },
  blue: { btn: 'border-blue-400 bg-blue-50 text-blue-700', icon: 'text-blue-500', dot: 'bg-blue-400' },
  amber: { btn: 'border-amber-400 bg-amber-50 text-amber-700', icon: 'text-amber-500', dot: 'bg-amber-400' },
  orange: { btn: 'border-orange-400 bg-orange-50 text-orange-700', icon: 'text-orange-500', dot: 'bg-orange-400' },
  gray: { btn: 'border-gray-400 bg-gray-100 text-gray-700', icon: 'text-gray-500', dot: 'bg-gray-400' },
};

export default function LogCallModal({ lead, currentUser, onClose, onSave, onSaveLead, venues, eventTypes }) {
  const { t, language } = useLanguage();
  const { syncLeadEvent, shouldAutoSync } = useGoogleCalendar();

  const [activeTab, setActiveTab] = useState('call');
  const [callLog, setCallLog] = useState({ outcome: '', notes: '', nextCallDate: '', nextCallTime: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Lead Details State
  const [formData, setFormData] = useState({ ...lead });
  const isLockedStatus = ['Completed'].includes(formData.status) || ['Completed'].includes(formData.stage);

  const [stageChangeConfirm, setStageChangeConfirm] = useState(null);
  const [lossReason, setLossReason] = useState('');
  const [stageChangeNote, setStageChangeNote] = useState('');

  const [bookingConfirm, setBookingConfirm] = useState(null);
  const [bookingData, setBookingData] = useState({
    venue: '', eventDate: '', eventType: '', guests: '',
    finalAmount: '', advanceAmount: '', shift: '',
    notes: '', clientCnic: '', clientAddress: '', clientEmail: ''
  });
  const [venueCheckStatus, setVenueCheckStatus] = useState(null); // null | 'checking' | 'available' | 'booked'
  const [venueBookedInfo, setVenueBookedInfo] = useState(null);

  // Quotation Items State
  const [quotationItems, setQuotationItems] = useState(() => {
    const s = lead.quotationItems || {};
    return {
      lawnRent: { lumpsum: s.lawnRent?.lumpsum || '', discount: s.lawnRent?.discount || 0 },
      decoration: { lumpsum: s.decoration?.lumpsum || '', discount: s.decoration?.discount || 0 },
      coldDrink: { enabled: s.coldDrink?.enabled || 'no', rate: s.coldDrink?.rate || '', discount: s.coldDrink?.discount || 0, inputMode: s.coldDrink?.inputMode || 2, lumpsum: s.coldDrink?.lumpsum || '' },
      mineralWater: { enabled: s.mineralWater?.enabled || 'no', rate: s.mineralWater?.rate || '', discount: s.mineralWater?.discount || 0, inputMode: s.mineralWater?.inputMode || 2, lumpsum: s.mineralWater?.lumpsum || '' },
      pantryCharges: { enabled: s.pantryCharges?.enabled || 'no', rate: s.pantryCharges?.rate || '', discount: s.pantryCharges?.discount || 0 },
      freshFlower: { enabled: s.freshFlower?.enabled || 'no', rate: s.freshFlower?.rate || '', discount: s.freshFlower?.discount || 0 },
      addons: s.addons || []
    };
  });

  const d = new Date();
  const todayStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

  const isPastEventDate = useMemo(() => {
    if (!formData.eventDate) return false;
    const eventDateVal = new Date(formData.eventDate).toISOString().split('T')[0];
    return eventDateVal < todayStr;
  }, [formData.eventDate, todayStr]);

  // --- Quotation Calculations ---
  const quotationCalc = useMemo(() => {
    const g = Number(bookingConfirm ? bookingData.guests : formData.guests) || 0;
    const lump = (l, d) => Math.max(0, (Number(l) || 0) - (Number(d) || 0));
    const lrSub = lump(quotationItems.lawnRent.lumpsum, quotationItems.lawnRent.discount);
    const decSub = lump(quotationItems.decoration.lumpsum, quotationItems.decoration.discount);

    const cdRawSub = quotationItems.coldDrink.enabled === 'yes'
      ? (quotationItems.coldDrink.inputMode === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : (Number(quotationItems.coldDrink.rate) || 0) * g) : 0;
    const cdSub = Math.max(0, cdRawSub - (Number(quotationItems.coldDrink.discount) || 0));

    const mwRawSub = quotationItems.mineralWater.enabled === 'yes'
      ? (quotationItems.mineralWater.inputMode === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : (Number(quotationItems.mineralWater.rate) || 0) * g) : 0;
    const mwSub = Math.max(0, mwRawSub - (Number(quotationItems.mineralWater.discount) || 0));

    const cdPerPax = g > 0 && quotationItems.coldDrink.enabled === 'yes' && quotationItems.coldDrink.inputMode === 1 ? (cdSub / g) : null;
    const mwPerPax = g > 0 && quotationItems.mineralWater.enabled === 'yes' && quotationItems.mineralWater.inputMode === 1 ? (mwSub / g) : null;

    const pcSub = quotationItems.pantryCharges.enabled === 'yes' ? lump(quotationItems.pantryCharges.rate, quotationItems.pantryCharges.discount) : 0;
    const ffSub = quotationItems.freshFlower.enabled === 'yes' ? lump(quotationItems.freshFlower.rate, quotationItems.freshFlower.discount) : 0;

    const addonsSub = quotationItems.addons ? quotationItems.addons.reduce((sum, item) => sum + Math.max(0, (Number(item.lumpsum) || 0) - (Number(item.discount) || 0)), 0) : 0;

    const total = lrSub + decSub + cdSub + mwSub + pcSub + ffSub + addonsSub;
    return { lrSub, decSub, cdSub, mwSub, pcSub, ffSub, addonsSub, total, g, cdPerPax, mwPerPax };
  }, [quotationItems, formData.guests, bookingConfirm, bookingData.guests]);



  const calculateNextCall = (outcomeId) => {
    const today = new Date();
    let nextDate = new Date();

    switch (outcomeId) {
      case 'busy':
        nextDate.setHours(today.getHours() + 2);
        if (nextDate.getDate() !== today.getDate()) {
          nextDate = new Date(today);
          nextDate.setDate(today.getDate() + 1);
          nextDate.setHours(10, 0, 0, 0);
        }
        break;
      case 'no_answer':
      case 'connected_callback': {
        nextDate.setDate(today.getDate() + 1);
        const h = today.getHours();
        if (h < 9 || h > 18) nextDate.setHours(10, 0, 0, 0);
        break;
      }
      case 'connected_interested':
        nextDate.setDate(today.getDate() + 2);
        nextDate.setHours(11, 0, 0, 0);
        break;
      case 'wrong_number':
      case 'connected_not_interested':
        return { date: '', time: '' };
      default:
        nextDate.setDate(today.getDate() + 1);
        nextDate.setHours(10, 0, 0, 0);
    }

    const dateStr = nextDate.toISOString().split('T')[0];
    const hh = nextDate.getHours().toString().padStart(2, '0');
    const mm = nextDate.getMinutes().toString().padStart(2, '0');
    return { date: dateStr, time: `${hh}:${mm}` };
  };


  const handleOutcomeSelect = (outcomeId) => {
    const { date, time } = calculateNextCall(outcomeId);
    setCallLog({ ...callLog, outcome: outcomeId, nextCallDate: date, nextCallTime: time });
  };

  const handleStageClick = (s) => {
    if (s === formData.stage) return;
    if (s === 'Won' || s === 'Site Visited') {
      setBookingData({
        venue: formData.venue || '',
        eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : '',
        eventType: formData.eventType || '',
        guests: formData.guests || '',
        finalAmount: quotationCalc.total || formData.quotationAmount || '',
        advanceAmount: formData.advancePaid || '',
        shift: formData.shift || '',
        notes: formData.bookingNotes || '',
        clientCnic: formData.clientCnic || '',
        clientAddress: formData.clientAddress || '',
        clientEmail: formData.email || ''
      });
      setBookingConfirm({ to: s });
      // Auto-check availability when modal opens with pre-filled data
      const preVenue = formData.venue || '';
      const preDate = formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : '';
      if (preVenue && preDate) {
        const preShift = formData.shift || '';
        setTimeout(() => checkVenueAvailability(preVenue, preDate, preShift), 0);
      }
    } else {
      setStageChangeConfirm({ from: formData.stage, to: s });
    }
  };

  const handleStageChangeConfirm = () => {
    if (!stageChangeConfirm) return;
    setFormData({ ...formData, stage: stageChangeConfirm.to });
    const wasSaveAction = stageChangeConfirm.isSaveAction;
    setStageChangeConfirm(null);
    if (wasSaveAction) {
      setTimeout(() => {
        const btn = document.getElementById('log-call-save-btn');
        if (btn) btn.click();
      }, 50);
    }
  };

  const handleStageChangeCancel = () => {
    setStageChangeConfirm(null);
    setLossReason('');
    setStageChangeNote('');
  };

  const handleBookingConfirm = async () => {
    if (!bookingConfirm) return;
    const finalAmt = quotationCalc.total || bookingData.finalAmount;
    if (!bookingData.venue || !bookingData.eventDate || bookingData.eventDate < todayStr || !finalAmt) return;
    if (bookingConfirm.to === 'Won' && (!bookingData.clientCnic || !bookingData.clientAddress)) return;
    setFormData(prev => ({
      ...prev,
      stage: bookingConfirm.to,
      venue: bookingData.venue,
      eventDate: bookingData.eventDate,
      eventType: bookingData.eventType,
      guests: bookingData.guests,
      quotationAmount: Number(finalAmt),
      shift: bookingData.shift,
      clientCnic: bookingData.clientCnic,
      clientAddress: bookingData.clientAddress,
      bookingNotes: bookingData.notes,
      bookedAt: new Date().toISOString(),
      bookedBy: currentUser?.name
    }));
    setBookingConfirm(null);
  };

  const handleBookingCancel = () => {
    setBookingConfirm(null);
    setVenueCheckStatus(null);
    setVenueBookedInfo(null);
  };

  // ── Venue Availability Check ──
  const checkVenueAvailability = async (venueId, eventDate, shiftName) => {
    if (!venueId || !eventDate || !shiftName) {
      setVenueCheckStatus(null);
      setVenueBookedInfo(null);
      return;
    }
    setVenueCheckStatus('checking');
    setVenueBookedInfo(null);
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = import.meta.env.VITE_API_URL ||
        (['localhost', '127.0.0.1'].includes(window.location.hostname)
          ? 'http://localhost:4009/api'
          : 'https://crmdemo.inplsoftwares.com/api');
      const res = await fetch(
        `${API_BASE_URL}/bookings/banquet-list?from_date=${eventDate}&to_date=${eventDate}`,
        { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      const data = await res.json();
      const bookingsList = data.data || [];
      const venueObj = (venues && venues.length > 0 ? venues : []).find(v => v.id === venueId);
      const venueName = venueObj?.name || '';
      const venueNumericId = venueObj?.venue_id ?? venueObj?.numericId ?? venueObj?.venueId ?? null;

      // Helper: extract meaningful words (3+ chars, not pure numbers)
      const meaningfulWords = (str) =>
        str.toLowerCase().split(/[\s_\-]+/).filter(w => w.length >= 3 && isNaN(w));

      const conflict = bookingsList.find(b => {
        const lawnName = (b.lawn_name || '').toLowerCase().trim();
        const vName = venueName.toLowerCase().trim();
        if (!lawnName || !vName) return false;

        let isVenueMatch = false;
        // Tier 1: numeric venue_id match (most reliable)
        if (venueNumericId && b.lawn_id && Number(b.lawn_id) === Number(venueNumericId)) isVenueMatch = true;
        // Tier 2: exact or substring match
        else if (lawnName === vName || lawnName.includes(vName) || vName.includes(lawnName)) isVenueMatch = true;
        // Tier 3: word-level fuzzy match
        else {
          const words1 = meaningfulWords(lawnName);
          const words2 = meaningfulWords(vName);
          isVenueMatch = words1.some(w1 => words2.some(w2 => w1.includes(w2) || w2.includes(w1)));
        }

        if (!isVenueMatch) return false;

        // Shift match check
        const bShift = (b.booking_timing_name || '').toLowerCase().trim();
        const targetShift = (shiftName || '').toLowerCase().trim();

        if (targetShift === 'day' || targetShift === 'morning') {
          if (bShift !== 'day' && bShift !== 'morning') return false;
        } else if (targetShift === 'night' || targetShift === 'evening') {
          if (bShift !== 'night' && bShift !== 'evening') return false;
        } else {
          if (bShift !== targetShift) return false;
        }

        return true;
      });
      if (conflict) {
        setVenueCheckStatus('booked');
        setVenueBookedInfo({
          clientName: conflict.customer_name || 'Unknown',
          orderNo: conflict.order_no || '',
          shift: conflict.booking_timing_name || '',
          functionName: conflict.function_name || '',
        });
      } else {
        setVenueCheckStatus('available');
        setVenueBookedInfo(null);
      }
    } catch (err) {
      console.error('Venue availability check failed:', err);
      setVenueCheckStatus(null);
    }
  };

  const handleSaveCall = async () => {
    if (!callLog.outcome) {
      setActiveTab('call');
      return;
    }

    if (isPastEventDate) {
      console.warn('Save blocked: event date is in the past.');
      return;
    }

    const lockedStages = ['Completed'];
    const isLeadLocked = lockedStages.includes(lead.stage);
    const isLostOutcome = callLog.outcome === 'wrong_number' || callLog.outcome === 'connected_not_interested';

    if (isLostOutcome && !isLeadLocked && formData.stage !== 'Lost') {
      if (!lossReason) {
        setStageChangeConfirm({ from: formData.stage, to: 'Lost', isSaveAction: true });
        return;
      }
    }

    const friendlyOutcome = CALL_OUTCOMES.find(o => o.id === callLog.outcome)?.label || callLog.outcome;
    const lostReasonMessage = isLostOutcome ? `Lost due to: ${friendlyOutcome}` : '';

    let updatedNotes = callLog.notes;
    if (isLostOutcome && !isLeadLocked) {
      updatedNotes = callLog.notes ? `${callLog.notes}\n\n${lostReasonMessage}` : lostReasonMessage;
    }

    const savePayload = {
      outcome: callLog.outcome,
      notes: updatedNotes,
      nextCallDate: callLog.nextCallDate || null,
      nextCallTime: callLog.nextCallTime || null,
      calledAt: new Date().toISOString(),
      calledBy: currentUser?.name,

      stage: (isLostOutcome && !isLeadLocked) ? 'Lost' : formData.stage,
      leadNotes: (isLostOutcome && !isLeadLocked)
        ? (lead.notes ? `${lead.notes}\n\nCall Outcome: ${lostReasonMessage}` : `Call Outcome: ${lostReasonMessage}`)
        : formData.notes,
      eventDate: formData.eventDate,
      eventType: formData.eventType,
      venue: formData.venue,
      guests: formData.guests ? Number(formData.guests) : null,
      shift: formData.shift,
      clientBudget: formData.clientBudget ? Number(formData.clientBudget) : null,
      siteVisitDate: formData.siteVisitDate,
      siteVisitTime: formData.siteVisitTime,
      siteVisited: formData.siteVisited,
      clientCnic: formData.clientCnic,
      clientAddress: formData.clientAddress,
      bookingNotes: formData.bookingNotes,
      bookedAt: formData.bookedAt,
      bookedBy: formData.bookedBy,
      quotationAmount: quotationCalc.total || formData.quotationAmount,
      quotationItems: quotationItems,
      lostReason: ((isLostOutcome && !isLeadLocked) || formData.stage === 'Lost') ? (lossReason || null) : null,
      lostReasonNote: ((isLostOutcome && !isLeadLocked) || formData.stage === 'Lost') ? (stageChangeNote || null) : null,
      ...(((isLostOutcome && !isLeadLocked) ? 'Lost' : formData.stage) === 'Won' && lead.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {})
    };

    setSaving(true);
    try {
      // ── Submit to BMS FIRST for bookings (only if stage changed) ──
      const isStageChanged = savePayload.stage !== lead.stage;
      if (isStageChanged && (savePayload.stage === 'Site Visited' || (savePayload.stage === 'Won' && savePayload.clientCnic))) {
        try {
          const g = parseInt(savePayload.guests) || 0;
          const venueObj = (venues && venues.length > 0 ? venues : VENUES).find(v => v.id === savePayload.venue);
          const venueNumericId = venueObj?.venue_id ?? venueObj?.numericId ?? venueObj?.venueId ?? (Number.isInteger(Number(savePayload.venue)) ? Number(savePayload.venue) : null);
          const shiftId = savePayload.shift === 'Evening' ? 19 : 20;

          const etList = eventTypes && eventTypes.length > 0 ? eventTypes : EVENT_TYPES;
          const etObj = etList.find(et => {
            const n = typeof et === 'string' ? et : et.name;
            return n === savePayload.eventType || et?.id === savePayload.eventType;
          });
          const eventTypeId = etObj ? (typeof etObj === 'object' && etObj.event_type_id ? etObj.event_type_id : etList.findIndex(et => (typeof et === 'string' ? et : et.name) === savePayload.eventType) + 1) : null;

          // Helper: is service enabled?
          const isEnabled = (val) => val === 'yes' || val === true || val === 1;

          // type_*: 1=LumpSum, 2=PerPerson (inputMode: 1=lumpsum, 2=perPax)
          const lrType = 1; // Lawn Rent is always LumpSum
          const decType = 1; // Decoration is always LumpSum
          const cdType = quotationItems.coldDrink.inputMode === 1 ? 1 : 2;
          const mwType = quotationItems.mineralWater.inputMode === 1 ? 1 : 2;

          // subtotals
          const lrSub = Math.max(0, (Number(quotationItems.lawnRent.lumpsum) || 0) - (Number(quotationItems.lawnRent.discount) || 0));
          const decSub = Math.max(0, (Number(quotationItems.decoration.lumpsum) || 0) - (Number(quotationItems.decoration.discount) || 0));
          const cdRaw = isEnabled(quotationItems.coldDrink.enabled)
            ? (cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : (Number(quotationItems.coldDrink.rate) || 0) * g)
            : 0;
          const cdSub = Math.max(0, cdRaw - (Number(quotationItems.coldDrink.discount) || 0));
          const mwRaw = isEnabled(quotationItems.mineralWater.enabled)
            ? (mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : (Number(quotationItems.mineralWater.rate) || 0) * g)
            : 0;
          const mwSub = Math.max(0, mwRaw - (Number(quotationItems.mineralWater.discount) || 0));
          const pcSub = isEnabled(quotationItems.pantryCharges.enabled) ? Math.max(0, (Number(quotationItems.pantryCharges.rate) || 0) - (Number(quotationItems.pantryCharges.discount) || 0)) : 0;
          const ffSub = isEnabled(quotationItems.freshFlower.enabled) ? Math.max(0, (Number(quotationItems.freshFlower.rate) || 0) - (Number(quotationItems.freshFlower.discount) || 0)) : 0;


          let externalPayload;

          if (savePayload.stage === 'Site Visited') {
            externalPayload = {
              inquiry_type: 2,
              order_date: todayStr.split('-').reverse().join('-'),
              function_date: savePayload.eventDate.split('-').reverse().join('-'),
              validity_date: todayStr.split('-').reverse().join('-'),
              lawn_id: venueObj?.venue_id ?? venueObj?.numericId ?? venueNumericId,
              lawn_marge_status: 0,
              booking_timing: shiftId,
              customer_name: lead.clientName,
              mobile_one: lead.phone || "-",
              mobile_two: "-",
              address: savePayload.clientAddress || savePayload.clientAddress || "-",
              cnic: savePayload.clientCnic || savePayload.clientCnic || "-",
              function_id: eventTypeId,
              no_of_guest: g,
              allow_guest: g,
              quotation: Number(savePayload.quotationAmount) || 0,
              note: savePayload.bookingNotes || 'Tentative booking from CRM',
              grn_no: 0,
              receipt_no: 0,
              stock_type: 1,

              decoration: (Number(quotationItems.decoration.lumpsum) || 0) > 0 ? 1 : 0,
              stage: 1,
              stage_layer_id: 1,
              cold_drink: isEnabled(quotationItems.coldDrink.enabled) ? 1 : 0,
              mineral_water: isEnabled(quotationItems.mineralWater.enabled) ? 1 : 0,
              pantry_charges: isEnabled(quotationItems.pantryCharges.enabled) ? 1 : 0,
              fresh_flower: isEnabled(quotationItems.freshFlower.enabled) ? 1 : 0,

              service_lawn_rent: 'Lawn Rent',
              type_lawn_rent: lrType,
              lumpsum_lawn_rent: Number(quotationItems.lawnRent.lumpsum) || 0,
              rate_lawn_rent: 0,
              booked_qty_lawn_rent: g,
              discount_lawn_rent: Number(quotationItems.lawnRent.discount) || 0,
              discount_description_lawn_rent: '-',
              sub_total_lawn_rent: lrSub,

              service_decoration: 'Decoration',
              type_decoration: decType,
              lumpsum_decoration: Number(quotationItems.decoration.lumpsum) || 0,
              rate_decoration: 0,
              booked_qty_decoration: g,
              discount_decoration: Number(quotationItems.decoration.discount) || 0,
              discount_description_decoration: '-',
              sub_total_decoration: decSub,

              type_stage: 1,
              lumpsum_stage: 0,
              rate_stage: 0,
              booked_qty_stage: g,
              discount_stage: 0,
              discount_description_stage: '-',
              sub_total_stage: 0,

              service_cold_drink: 'Cold Drink',
              type_cold_drink: cdType,
              lumpsum_cold_drink: cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : cdRaw,
              rate_cold_drink: cdType === 2 ? (Number(quotationItems.coldDrink.rate) || 0) : 0,
              booked_qty_cold_drink: g,
              discount_cold_drink: Number(quotationItems.coldDrink.discount) || 0,
              discount_description_cold_drink: '-',
              sub_total_cold_drink: cdSub,

              service_mineral_water: 'Mineral Water',
              type_mineral_water: mwType,
              lumpsum_mineral_water: mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : mwRaw,
              rate_mineral_water: mwType === 2 ? (Number(quotationItems.mineralWater.rate) || 0) : 0,
              booked_qty_mineral_water: g,
              discount_mineral_water: Number(quotationItems.mineralWater.discount) || 0,
              discount_description_mineral_water: '-',
              sub_total_mineral_water: mwSub,

              service_pantry_charges: 'Pantry Charges',
              type_pantry_charges: 1,
              lumpsum_pantry_charges: Number(quotationItems.pantryCharges.rate) || 0,
              rate_pantry_charges: 0,
              booked_qty_pantry_charges: g,
              discount_pantry_charges: Number(quotationItems.pantryCharges.discount) || 0,
              discount_description_pantry_charges: '-',
              sub_total_pantry_charges: pcSub,

              service_fresh_flower: 'Flower Work',
              type_fresh_flower: 1,
              lumpsum_fresh_flower: Number(quotationItems.freshFlower.rate) || 0,
              rate_fresh_flower: 0,
              booked_qty_fresh_flower: g,
              discount_fresh_flower: Number(quotationItems.freshFlower.discount) || 0,
              discount_description_fresh_flower: '-',
              sub_total_fresh_flower: ffSub,

              additional_services: (quotationItems.addons || []).filter(a => a.name && (Number(a.lumpsum) || 0) > 0).map(a => {
                const ls = Number(a.lumpsum) || 0;
                const dsc = Number(a.discount) || 0;
                const sub = Math.max(0, ls - dsc);
                return {
                  service_name: a.name,
                  rate_type: 1,
                  lumpsum: ls,
                  rate: g > 0 ? Number((ls / g).toFixed(2)) : 0,
                  booked_qty: g,
                  discount: dsc,
                  discount_description: '-',
                  sub_total: sub
                };
              })
            };
          } else {
            externalPayload = {
              crm_id: `CRM-${lead.id}`,
              client_name: lead.clientName,
              client_mobile: lead.phone,
              email_address: lead.email || lead.email || '',
              cnic_no: savePayload.clientCnic,
              address: savePayload.clientAddress,
              venue_id: venueObj?.venue_id ?? venueObj?.numericId ?? venueNumericId,
              event_date: savePayload.eventDate,
              shift_id: shiftId,
              event_type_id: eventTypeId,
              guests: g,
              allow_guest: g,
              final_invoice_amount: Number(savePayload.quotationAmount),
              advance_received: Number(0) || 0,
              booking_notes: savePayload.bookingNotes || '',

              // Service flags (1=included, 0=not)
              decoration: (Number(quotationItems.decoration.lumpsum) || 0) > 0 ? 1 : 0,
              cold_drink: isEnabled(quotationItems.coldDrink.enabled) ? 1 : 0,
              mineral_water: isEnabled(quotationItems.mineralWater.enabled) ? 1 : 0,
              pantry_charges: isEnabled(quotationItems.pantryCharges.enabled) ? 1 : 0,
              fresh_flower: isEnabled(quotationItems.freshFlower.enabled) ? 1 : 0,

              // Lawn Rent
              service_lawn_rent: 'Lawn Rent',
              type_lawn_rent: lrType,
              lumpsum_lawn_rent: Number(quotationItems.lawnRent.lumpsum) || 0,
              rate_lawn_rent: 0,
              booked_qty_lawn_rent: g,
              discount_lawn_rent: Number(quotationItems.lawnRent.discount) || 0,
              discount_description_lawn_rent: '-',
              sub_total_lawn_rent: lrSub,

              // Decoration
              service_decoration: 'Decoration',
              type_decoration: decType,
              lumpsum_decoration: Number(quotationItems.decoration.lumpsum) || 0,
              rate_decoration: 0,
              booked_qty_decoration: g,
              discount_decoration: Number(quotationItems.decoration.discount) || 0,
              discount_description_decoration: '-',
              sub_total_decoration: decSub,

              // Cold Drink
              service_cold_drink: 'Cold Drink',
              type_cold_drink: cdType,
              lumpsum_cold_drink: cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : cdRaw,
              rate_cold_drink: cdType === 2 ? (Number(quotationItems.coldDrink.rate) || 0) : 0,
              booked_qty_cold_drink: g,
              discount_cold_drink: Number(quotationItems.coldDrink.discount) || 0,
              discount_description_cold_drink: '-',
              sub_total_cold_drink: cdSub,

              // Mineral Water
              service_mineral_water: 'Mineral Water',
              type_mineral_water: mwType,
              lumpsum_mineral_water: mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : mwRaw,
              rate_mineral_water: mwType === 2 ? (Number(quotationItems.mineralWater.rate) || 0) : 0,
              booked_qty_mineral_water: g,
              discount_mineral_water: Number(quotationItems.mineralWater.discount) || 0,
              discount_description_mineral_water: '-',
              sub_total_mineral_water: mwSub,

              // Pantry Charges
              service_pantry_charges: 'Pantry Charges',
              type_pantry_charges: 1,
              lumpsum_pantry_charges: Number(quotationItems.pantryCharges.rate) || 0,
              rate_pantry_charges: 0,
              booked_qty_pantry_charges: g,
              discount_pantry_charges: Number(quotationItems.pantryCharges.discount) || 0,
              discount_description_pantry_charges: '-',
              sub_total_pantry_charges: pcSub,

              // Flower Work
              service_fresh_flower: 'Flower Work',
              type_fresh_flower: 1,
              lumpsum_fresh_flower: Number(quotationItems.freshFlower.rate) || 0,
              rate_fresh_flower: 0,
              booked_qty_fresh_flower: g,
              discount_fresh_flower: Number(quotationItems.freshFlower.discount) || 0,
              discount_description_fresh_flower: '-',
              sub_total_fresh_flower: ffSub,

              additional_services: (quotationItems.addons || []).filter(a => a.name && (Number(a.lumpsum) || 0) > 0).map(a => {
                const ls = Number(a.lumpsum) || 0;
                const dsc = Number(a.discount) || 0;
                const sub = Math.max(0, ls - dsc);
                return {
                  service_name: a.name,
                  rate_type: 1,
                  lumpsum: ls,
                  rate: g > 0 ? Number((ls / g).toFixed(2)) : 0,
                  booked_qty: g,
                  discount: dsc,
                  discount_description: '-',
                  sub_total: sub
                };
              })
            };
          } // Close else block



          if ((externalPayload.venue_id || externalPayload.lawn_id) && (externalPayload.event_type_id || externalPayload.function_id)) {
            if (savePayload.stage === 'Site Visited') {
              const apiRes = await bookingService.submitTentativeToExternal(externalPayload);
              if (apiRes && (apiRes.status === false || apiRes.success === false || apiRes.error)) {
                throw new Error(apiRes.error || apiRes.message || JSON.stringify(apiRes));
              }
            } else {
              await bookingService.submitToExternal(externalPayload);
            }
          } else {
            throw new Error('Venue or Event Type Banquet ID missing.');
          }
        } catch (extError) {
          console.error('External banquet API error:', extError);
          setToast({ message: 'Banquet System Error: ' + (extError.message || 'Failed to post booking.'), type: 'error' });
          setSaving(false);
          return; // Block CRM save
        }
      }

      await onSave(lead.id, savePayload);
      onClose();

      if (onSaveLead) {
        const leadUpdatePayload = {
          stage: savePayload.stage,
          eventDate: savePayload.eventDate ? new Date(savePayload.eventDate).toISOString() : null,
          eventType: savePayload.eventType,
          venue: savePayload.venue,
          guests: savePayload.guests,
          shift: savePayload.shift,
          clientBudget: savePayload.clientBudget,
          siteVisitDate: savePayload.siteVisitDate,
          siteVisitTime: savePayload.siteVisitTime,
          siteVisited: savePayload.siteVisited,
          clientCnic: savePayload.clientCnic,
          clientAddress: savePayload.clientAddress,
          bookingNotes: savePayload.bookingNotes,
          bookedAt: savePayload.bookedAt,
          bookedBy: savePayload.bookedBy,
          quotationAmount: savePayload.quotationAmount,
          quotationItems: savePayload.quotationItems,
          lostReason: savePayload.lostReason,
          lostReasonNote: savePayload.lostReasonNote,
          ...(savePayload.order_date ? { order_date: savePayload.order_date } : {})
        };
        try {
          onSaveLead(lead.id, leadUpdatePayload).catch(e => console.error(e));
        } catch (e) {
          console.error("Error updating lead details:", e);
        }
      }

      if (callLog.nextCallDate && shouldAutoSync('call') && onSaveLead && !isLostOutcome) {
        try {
          const updatedLead = { ...lead, ...savePayload, nextCallDate: callLog.nextCallDate, nextCallTime: callLog.nextCallTime, lastCallNotes: callLog.notes };
          syncLeadEvent(updatedLead, 'call', onSaveLead).catch(e => console.error(e));
        } catch (calendarError) {
          console.error('Calendar sync error:', calendarError);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving call log:', error);
    } finally {
      setSaving(false);
    }
  };


  const isBookingFormValid = bookingData.venue && bookingData.eventDate && Number(bookingData.guests) > 0 && (quotationCalc.total || bookingData.finalAmount) && venueCheckStatus !== 'booked' && (bookingConfirm?.to === 'Site Visited' ? true : (bookingData.clientCnic && bookingData.clientAddress));

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-5xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[96vh] sm:max-h-[90vh] animate-scale-in overflow-hidden">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <PhoneCall className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                {language === 'ur' ? 'کال لاگ کریں' : 'Log Call'}
              </h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider ml-2 ${formData.stage === 'Won' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                formData.stage === 'Lost' ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-slate-100 text-slate-700 border-slate-200'
                }`}>
                {formData.stage}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 ml-0.5">
              <span className="font-semibold text-gray-800 text-sm">{lead.clientName}</span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Phone className="w-3 h-3" />{lead.phone}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full shrink-0 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-200 bg-slate-50 shrink-0">
          <button
            onClick={() => setActiveTab('call')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${activeTab === 'call' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
          >
            <PhoneCall className="w-4 h-4" /> Call Details
          </button>
          <button
            onClick={() => setActiveTab('quote')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${activeTab === 'quote' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
          >
            <FileText className="w-4 h-4" /> Requirements & Quote
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {activeTab === 'call' ? (
            <div className="p-4 sm:p-6 space-y-4">
              {/* Quick call buttons */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  {language === 'ur' ? 'پہلے کال کریں' : 'Make the call first'}
                </p>
                <div className="flex gap-2">
                  <a href={`tel:${lead.phone}`} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                    <Phone className="w-4 h-4" />
                    {language === 'ur' ? 'فون کال' : 'Phone Call'}
                  </a>
                  <a href={getWhatsappLink(lead.phone)} target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </div>

              {/* Call Outcome */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  {language === 'ur' ? 'کال کا نتیجہ' : 'Call Outcome'} <span className="text-red-400 normal-case">*</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CALL_OUTCOMES.map((outcome) => {
                    const Icon = outcome.icon;
                    const isSelected = callLog.outcome === outcome.id;
                    const styles = OUTCOME_STYLES[outcome.color];
                    return (
                      <button
                        key={outcome.id}
                        onClick={() => handleOutcomeSelect(outcome.id)}
                        className={`p-2.5 rounded-xl border-2 text-left text-xs font-semibold flex items-center gap-2.5 transition-all ${isSelected ? styles.btn : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                          }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isSelected ? styles.icon : 'text-gray-400'}`} />
                        <span className="truncate leading-tight">
                          {language === 'ur' ? outcome.labelUr : outcome.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {t('notes')}
                </label>
                <textarea
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 resize-none transition-colors"
                  placeholder={t('callDetailsPlaceholder') || 'Call details...'}
                  value={callLog.notes}
                  onChange={(e) => setCallLog({ ...callLog, notes: e.target.value })}
                />
              </div>

              {/* Schedule Next Call */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-blue-500" />
                  {t('scheduleNextCall') || 'Schedule Next Call'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <DatePicker
                    value={callLog.nextCallDate}
                    onChange={(e) => setCallLog({ ...callLog, nextCallDate: e.target.value })}
                    min={todayStr}
                  />
                  <input
                    type="time"
                    className="p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={callLog.nextCallTime}
                    onChange={(e) => setCallLog({ ...callLog, nextCallTime: e.target.value })}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-4">
              {/* Stages */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 overflow-x-auto no-scrollbar">
                <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Lead Stage
                </h3>
                <div className="flex gap-1.5 min-w-max">
                  {STAGES.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStageClick(s)}
                      disabled={isLockedStatus}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border whitespace-nowrap ${isLockedStatus
                        ? 'opacity-40 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200'
                        : formData.stage === s
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                        }`}
                    >
                      {t(s) || s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Event Details Form */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Event Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('eventDate')}</label>
                    <DatePicker
                      value={formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                      disabled={isLockedStatus}
                      min={todayStr}
                    />
                    {isPastEventDate && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {t('eventDateCannotBeInPast') || 'Event date cannot be in the past.'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('shift')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                          disabled={isLockedStatus}
                        >
                          {formData.shift || t('selectShift') || 'Select Shift'}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([formData.shift])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setFormData({ ...formData, shift: Array.from(keys)[0] })}
                        >
                          <DropdownItem id="">{t('selectShift') || 'Select Shift'}</DropdownItem>
                          <DropdownItem id="Morning">Morning</DropdownItem>
                          <DropdownItem id="Evening">Evening</DropdownItem>
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('eventType')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                          disabled={isLockedStatus}
                        >
                          {formData.eventType || t('selectEventType') || 'Select Event Type'}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([formData.eventType])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setFormData({ ...formData, eventType: Array.from(keys)[0] })}
                        >
                          <DropdownItem id="">{t('selectEventType') || 'Select Event Type'}</DropdownItem>
                          {(eventTypes || EVENT_TYPES).map((et) => {
                            const etName = typeof et === 'string' ? et : et.name;
                            return <DropdownItem key={etName} id={etName}>{etName}</DropdownItem>;
                          })}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('venuePreference')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                          disabled={isLockedStatus}
                        >
                          {formData.venue ? (venues && venues.length > 0 ? venues : VENUES).find(v => v.id === formData.venue)?.name || formData.venue : t('selectVenue') || 'Select Venue'}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([formData.venue])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setFormData({ ...formData, venue: Array.from(keys)[0] })}
                        >
                          <DropdownItem id="">{t('selectVenue') || 'Select Venue'}</DropdownItem>
                          {(venues && venues.length > 0 ? venues : VENUES).map((v) => (
                            <DropdownItem key={v.id} id={v.id}>{v.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('guests')}</label>
                    <input
                      type="number" onWheel={(e) => e.target.blur()}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                      value={formData.guests || ''}
                      onChange={(e) => setFormData({ ...formData, guests: e.target.value.replace(/[^0-9]/g, '') })}
                      disabled={isLockedStatus}
                      placeholder="Guest count"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('clientBudget')}</label>
                    <input
                      type="number" onWheel={(e) => e.target.blur()}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                      value={formData.clientBudget || ''}
                      onChange={(e) => setFormData({ ...formData, clientBudget: e.target.value })}
                      disabled={isLockedStatus}
                      placeholder="Client budget"
                    />
                  </div>
                </div>
              </div>

              {/* Site Visit Schedule */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Site Visit Schedule
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('siteVisitDate') || 'Site Visit Date'}</label>
                    <DatePicker
                      value={formData.siteVisitDate ? new Date(formData.siteVisitDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, siteVisitDate: e.target.value })}
                      disabled={isLockedStatus}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('siteVisitTime') || 'Site Visit Time'}</label>
                    <input
                      type="time"
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                      value={formData.siteVisitTime || ''}
                      onChange={(e) => setFormData({ ...formData, siteVisitTime: e.target.value })}
                      disabled={isLockedStatus}
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-2 flex items-center mt-2">
                    <CustomCheckbox
                      name="siteVisited"
                      isSelected={formData.siteVisited || false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFormData({ ...formData, siteVisited: isChecked });
                        if (isChecked && formData.stage !== 'Site Visited' && formData.stage !== 'Won' && formData.stage !== 'Lost') {
                          handleStageClick('Site Visited');
                        }
                      }}
                      isDisabled={isLockedStatus}
                    >
                      {t('markSiteVisited') || 'Mark as Site Visited'}
                    </CustomCheckbox>
                  </div>
                </div>
              </div>

              {/* Quotation Builder Mini */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3 min-w-[500px]">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Quotation</h3>
                  </div>
                  <span className="text-[11px] text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                    Guests: <span className="text-blue-600 font-bold">{quotationCalc.g}</span>
                  </span>
                </div>

                <div className="overflow-x-auto -mx-1 px-1 pb-2"><div className="min-w-[700px] sm:min-w-0">
                  {/* Column Labels */}
                  <div className="grid grid-cols-12 gap-x-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">
                    <div className="col-span-3">{t('qbService') || 'Service'}</div>
                    <div className="col-span-1 text-center">{t('qbStatus') || 'Status'}</div>
                    <div className="col-span-2 text-center">{t('qbAmtRate') || 'Amt / Rate'}</div>
                    <div className="col-span-1 text-center">{t('qbQty') || 'Qty'}</div>
                    <div className="col-span-2 text-center">{t('qbSubtotal') || 'Subtotal'}</div>
                    <div className="col-span-1 text-center">{t('qbDisc') || 'Disc'}</div>
                    <div className="col-span-2 text-right">{t('qbNet') || 'Net'}</div>
                  </div>

                  <div className="space-y-2">

                    {/* ── Lawn Rent ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbLawnRent') || 'Lawn Rent'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.lawnRent.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.lawnRent.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.lawnRent.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.lrSub.toLocaleString()}</div>
                    </div>

                    {/* ── Decoration ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbDecoration') || 'Decoration'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.decoration.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.decoration.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.decoration.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.decSub.toLocaleString()}</div>
                    </div>

                    {/* ── Cold Drink ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbColdDrink') || 'Cold Drink'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.coldDrink.enabled === 'yes'
                              ? (quotationItems.coldDrink.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.coldDrink.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.coldDrink.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            quotationItems.coldDrink.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.coldDrink.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.coldDrink.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.coldDrink.enabled === 'yes'
                            ? (quotationItems.coldDrink.inputMode === 1
                              ? (Number(quotationItems.coldDrink.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.coldDrink.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.cdSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.coldDrink.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.coldDrink.inputMode === 1 && quotationCalc.cdPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.cdPerPax % 1 === 0 ? quotationCalc.cdPerPax.toLocaleString() : quotationCalc.cdPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.coldDrink.inputMode !== 1 && quotationItems.coldDrink.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Mineral Water ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbMineralWater') || 'Mineral Water'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.mineralWater.enabled === 'yes'
                              ? (quotationItems.mineralWater.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.mineralWater.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.mineralWater.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.mineralWater.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            quotationItems.mineralWater.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.mineralWater.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.mineralWater.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : quotationItems.mineralWater.enabled === 'complementary' ? (
                            <span className="text-[10px] text-green-600 font-semibold text-center block">4 Dispensers</span>
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.mineralWater.enabled === 'yes'
                            ? (quotationItems.mineralWater.inputMode === 1
                              ? (Number(quotationItems.mineralWater.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.mineralWater.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.mwSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.mineralWater.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.mineralWater.inputMode === 1 && quotationCalc.mwPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.mwPerPax % 1 === 0 ? quotationCalc.mwPerPax.toLocaleString() : quotationCalc.mwPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.mineralWater.inputMode !== 1 && quotationItems.mineralWater.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Pantry Charges ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbPantryCharges') || 'Pantry Charges'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.pantryCharges.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.pantryCharges.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.pantryCharges.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (Number(quotationItems.pantryCharges.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.pantryCharges.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.pantryCharges.enabled === 'yes' ? quotationCalc.pcSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* ── Flower Work ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbFlowerWork') || 'Flower Work'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.freshFlower.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.freshFlower.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.freshFlower.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.freshFlower.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : quotationItems.freshFlower.enabled === 'complementary' ? (
                          <span className="text-[10px] text-green-600 font-semibold text-center block">{t('qbOnStage') || 'On Stage'}</span>
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.freshFlower.enabled === 'yes' ? (Number(quotationItems.freshFlower.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.freshFlower.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.freshFlower.enabled === 'yes' ? quotationCalc.ffSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* Addons */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gray-600 uppercase">{t('qbAddons') || 'Addons'}</h4>
                        <button
                          type="button"
                          onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, addons: [...(p.addons || []), { id: Date.now(), name: '', lumpsum: '', discount: '' }] }))}
                          disabled={isLockedStatus}
                          className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                        >
                          + Add More
                        </button>
                      </div>

                      {(quotationItems.addons || []).map((addon, index) => (
                        <div key={addon.id || index} className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100 mt-2">
                          <div className="col-span-3">
                            <input type="text" className="w-full p-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:outline-none" placeholder={t('qbAddonName') || 'Addon Name'}
                              value={addon.name}
                              onChange={e => {
                                const newAddons = [...quotationItems.addons];
                                newAddons[index].name = e.target.value;
                                setQuotationItems(p => ({ ...p, addons: newAddons }));
                              }}
                              disabled={isLockedStatus} />
                          </div>
                          <div className="col-span-1 flex justify-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span></div>
                          <div className="col-span-2">
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                              value={addon.lumpsum}
                              onChange={e => {
                                const newAddons = [...quotationItems.addons];
                                newAddons[index].lumpsum = e.target.value;
                                setQuotationItems(p => ({ ...p, addons: newAddons }));
                              }}
                              disabled={isLockedStatus} />
                          </div>
                          <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                            {(Number(addon.lumpsum) || 0).toLocaleString()}
                          </div>
                          <div className="col-span-1">
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={addon.discount}
                              onChange={e => {
                                const newAddons = [...quotationItems.addons];
                                newAddons[index].discount = e.target.value;
                                setQuotationItems(p => ({ ...p, addons: newAddons }));
                              }}
                              disabled={isLockedStatus} />
                          </div>
                          <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">
                            {Math.max(0, (Number(addon.lumpsum) || 0) - (Number(addon.discount) || 0)).toLocaleString()}
                          </div>
                          <div className="col-span-1 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                if (isLockedStatus) return;
                                const newAddons = quotationItems.addons.filter((_, i) => i !== index);
                                setQuotationItems(p => ({ ...p, addons: newAddons }));
                              }}
                              disabled={isLockedStatus}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Grand Total */}
                    <div className="flex items-center justify-between bg-blue-600 text-white rounded-lg px-4 py-3 mt-1">
                      <span className="text-xs font-bold uppercase tracking-widest">{t('qbGrandTotal') || 'Grand Total'}</span>
                      <span className="text-base font-bold">PKR {quotationCalc.total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Banner for Past Event Date */}
        {isPastEventDate && (
          <div className="px-4 sm:px-6 py-2.5 bg-red-50 border-t border-red-100 flex items-center gap-2 text-red-600 text-xs font-medium shrink-0">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {language === 'ur' ? 'ایونٹ کی تاریخ ماضی میں نہیں ہو سکتی۔ براہ کرم Requirements & Quote میں جا کر اسے درست کریں۔' : 'Event date cannot be in the past. Please update it in the Requirements & Quote tab.'}
          </div>
        )}

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-white flex gap-2 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
            {t('cancel') || 'Cancel'}
          </button>
          <button
            id="log-call-save-btn"
            onClick={handleSaveCall}
            disabled={!callLog.outcome || saving || isPastEventDate}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <><Loader className="w-4 h-4 animate-spin" /><span>{t('saving') || 'Saving...'}</span></> : t('saveAndNext') || 'Save & Next'}
          </button>
        </div>

        {/* ── Booking Confirmation Modal ── */}
        {bookingConfirm && (
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-0 sm:p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) handleBookingCancel(); }}
            onTouchStart={(e) => { if (e.target === e.currentTarget) handleBookingCancel(); }}
          >
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl animate-scale-in flex flex-col max-h-[92vh] sm:max-h-[88vh]">
              {/* Header */}
              <div className="bg-white border-b border-gray-200 px-5 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {bookingConfirm.to === 'Site Visited' ? 'Confirm Tentative Booking' : t('confirmBookingTitle')}
                    </h3>
                    <p className="text-xs text-gray-500">{t('verifyDetails')}</p>
                  </div>
                </div>
                <button onClick={handleBookingCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
                {/* Client Info */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{lead.clientName}</p>
                    <p className="text-xs text-gray-500">{lead.phone}</p>
                  </div>
                </div>

                {/* CNIC and Address */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        CNIC No <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength="15"
                        className={`w-full p-2.5 border rounded-lg text-sm ${!bookingData.clientCnic ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={bookingData.clientCnic}
                        onChange={(e) => setBookingData({ ...bookingData, clientCnic: e.target.value })}
                        placeholder="e.g., 42101-1234567-1"
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2.5 border rounded-lg text-sm ${!bookingData.clientAddress ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={bookingData.clientAddress}
                        onChange={(e) => setBookingData({ ...bookingData, clientAddress: e.target.value })}
                        placeholder="Client Address"
                        disabled={isLockedStatus}
                      />
                    </div>
                  </div>
                )}

                {/* Venue + Event Date — same row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('venue')} <span className="text-red-500">*</span>
                    </label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className={`w-full p-2.5 border rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-left flex justify-between items-center ${!bookingData.venue ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isLockedStatus}
                        >
                          {bookingData.venue ? ((venues && venues.length > 0 ? venues : VENUES).find(v => v.id === bookingData.venue)?.name || bookingData.venue) : (t('selectVenue') || 'Select Venue')}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([bookingData.venue])}
                          selectionMode="single"
                          onSelectionChange={(keys) => {
                            const newVenue = Array.from(keys)[0];
                            setBookingData({ ...bookingData, venue: newVenue });
                            checkVenueAvailability(newVenue, bookingData.eventDate, bookingData.shift);
                          }}
                        >
                          {(venues && venues.length > 0 ? venues.filter(v => v.isLive !== false) : VENUES).map((v) => (
                            <DropdownItem key={v.id} id={v.id}>{v.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                    {!bookingData.venue && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {t('pleaseSelectVenue')}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('eventDate')} <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      className={`${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                      error={!bookingData.eventDate ? ' ' : null}
                      value={bookingData.eventDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setBookingData({ ...bookingData, eventDate: newDate });
                        checkVenueAvailability(bookingData.venue, newDate, bookingData.shift);
                      }}
                      disabled={isLockedStatus}
                      placement="bottom"
                    />
                  </div>
                </div>


                {/* Shift Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t('shift')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    {['Morning', 'Evening'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setBookingData({ ...bookingData, shift: s });
                          checkVenueAvailability(bookingData.venue, bookingData.eventDate, s);
                        }}
                        className={`flex-1 p-2.5 rounded-lg border-2 font-medium transition-all ${bookingData.shift === s
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                      >
                        {t(s.toLowerCase() + 'Shift') || s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Venue Availability Banner ── */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <>
                    {bookingData.shift && venueCheckStatus === 'checking' && (
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl animate-pulse">
                        <svg className="w-4 h-4 text-blue-500 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <p className="text-sm text-blue-700 font-medium">Checking venue availability...</p>
                      </div>
                    )}
                    {bookingData.shift && venueCheckStatus === 'booked' && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 rounded-xl">
                        <div className="shrink-0 p-1.5 bg-red-100 rounded-lg">
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-red-700">This venue is already booked on {bookingData.eventDate} in the {bookingData.shift} shift.</p>
                      </div>
                    )}
                    {bookingData.shift && venueCheckStatus === 'available' && bookingData.venue && bookingData.eventDate && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="shrink-0 p-1.5 bg-emerald-100 rounded-lg">
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-emerald-700">Venue is available on this date in the {bookingData.shift} shift.</p>
                      </div>
                    )}
                  </>
                )}


                {/* Event Type & Guests */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('eventType')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isLockedStatus}
                        >
                          {bookingData.eventType || t('selectEventType')}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([bookingData.eventType])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setBookingData({ ...bookingData, eventType: Array.from(keys)[0] })}
                        >
                          {(eventTypes || EVENT_TYPES).map((et, i) => {
                            const etName = typeof et === 'string' ? et : et.name;
                            return (
                              <DropdownItem key={`booking-et-${i}`} id={etName}>
                                {etName}
                              </DropdownItem>
                            );
                          })}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('guests')}</label>
                    <input
                      type="text"
                      className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                      value={bookingData.guests}
                      onChange={(e) => setBookingData({ ...bookingData, guests: e.target.value.replace(/[^0-9]/g, '') })}
                      placeholder={t('guestsPlaceholder')}
                      disabled={isLockedStatus}
                    />
                  </div>
                </div>

                {/* Quotation Builder */}
                <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('qbTitle') || 'Quotation Builder'}</h3>
                    </div>
                    <span className="text-[11px] text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                      Guests: <span className="text-blue-600 font-bold">{quotationCalc.g}</span>
                    </span>
                  </div>

                  <div className="overflow-x-auto -mx-1 px-1 pb-2"><div className="min-w-[700px] sm:min-w-0">
                    {/* Column Labels */}
                    <div className="grid grid-cols-12 gap-x-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">
                      <div className="col-span-3">{t('qbService') || 'Service'}</div>
                      <div className="col-span-1 text-center">{t('qbStatus') || 'Status'}</div>
                      <div className="col-span-2 text-center">{t('qbAmtRate') || 'Amt / Rate'}</div>
                      <div className="col-span-1 text-center">{t('qbQty') || 'Qty'}</div>
                      <div className="col-span-2 text-center">{t('qbSubtotal') || 'Subtotal'}</div>
                      <div className="col-span-1 text-center">{t('qbDisc') || 'Disc'}</div>
                      <div className="col-span-2 text-right">{t('qbNet') || 'Net'}</div>
                    </div>

                    <div className="space-y-2">

                      {/* ── Lawn Rent ── */}
                      <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbLawnRent') || 'Lawn Rent'}</p>
                          <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                        </div>
                        <div className="col-span-2">
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                            value={quotationItems.lawnRent.lumpsum}
                            onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, lumpsum: e.target.value } }))}
                            disabled={isLockedStatus} />
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.lawnRent.lumpsum) || 0).toLocaleString()}</div>
                        <div className="col-span-1">
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.lawnRent.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.lrSub.toLocaleString()}</div>
                      </div>

                      {/* ── Decoration ── */}
                      <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbDecoration') || 'Decoration'}</p>
                          <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                        </div>
                        <div className="col-span-2">
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                            value={quotationItems.decoration.lumpsum}
                            onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, lumpsum: e.target.value } }))}
                            disabled={isLockedStatus} />
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.decoration.lumpsum) || 0).toLocaleString()}</div>
                        <div className="col-span-1">
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.decoration.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.decSub.toLocaleString()}</div>
                      </div>

                      {/* ── Cold Drink ── */}
                      <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="grid grid-cols-12 gap-x-2 items-center">
                          <div className="col-span-3">
                            <p className="text-xs font-semibold text-gray-800">{t('qbColdDrink') || 'Cold Drink'}</p>
                            <p className="text-[10px] text-gray-400">
                              {quotationItems.coldDrink.enabled === 'yes'
                                ? (quotationItems.coldDrink.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                                : 'Guests × Rate'}
                            </p>
                          </div>
                          <div className="col-span-1">
                            <Dropdown>
                              <DropdownTrigger>
                                <button
                                  type="button"
                                  className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                  disabled={isLockedStatus}
                                >
                                  {quotationItems.coldDrink.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                                  <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                                </button>
                              </DropdownTrigger>
                              <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                <DropdownMenu
                                  selectedKeys={new Set([quotationItems.coldDrink.enabled])}
                                  selectionMode="single"
                                  onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, enabled: Array.from(keys)[0] } }))}
                                >
                                  <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                  <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                </DropdownMenu>
                              </DropdownPopover>
                            </Dropdown>
                          </div>
                          <div className="col-span-2">
                            {quotationItems.coldDrink.enabled === 'yes' ? (
                              quotationItems.coldDrink.inputMode === 1 ? (
                                <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                  value={quotationItems.coldDrink.lumpsum}
                                  onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, lumpsum: e.target.value } }))}
                                  disabled={isLockedStatus} />
                              ) : (
                                <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                  value={quotationItems.coldDrink.rate}
                                  onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, rate: e.target.value } }))}
                                  disabled={isLockedStatus} />
                              )
                            ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                          </div>
                          <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                          <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                            {quotationItems.coldDrink.enabled === 'yes'
                              ? (quotationItems.coldDrink.inputMode === 1
                                ? (Number(quotationItems.coldDrink.lumpsum) || 0).toLocaleString()
                                : ((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString())
                              : '—'}
                          </div>
                          <div className="col-span-1">
                            {quotationItems.coldDrink.enabled === 'yes' ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                                value={quotationItems.coldDrink.discount}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, discount: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                          </div>
                          <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.cdSub.toLocaleString() : '—'}</div>
                        </div>
                        {quotationItems.coldDrink.enabled === 'yes' && (
                          <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 2 } }))}
                                disabled={isLockedStatus}
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                              >
                                Per Person
                              </button>
                              <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 1 } }))}
                                disabled={isLockedStatus}
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                              >
                                Lump Sum
                              </button>
                            </div>
                            {quotationItems.coldDrink.inputMode === 1 && quotationCalc.cdPerPax !== null && (
                              <span className="text-[10px] text-purple-600 font-semibold">
                                = PKR {quotationCalc.cdPerPax % 1 === 0 ? quotationCalc.cdPerPax.toLocaleString() : quotationCalc.cdPerPax.toFixed(2)} / person
                              </span>
                            )}
                            {quotationItems.coldDrink.inputMode !== 1 && quotationItems.coldDrink.rate && quotationCalc.g > 0 && (
                              <span className="text-[10px] text-blue-500 font-medium">
                                Total = PKR {((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Mineral Water ── */}
                      <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="grid grid-cols-12 gap-x-2 items-center">
                          <div className="col-span-3">
                            <p className="text-xs font-semibold text-gray-800">{t('qbMineralWater') || 'Mineral Water'}</p>
                            <p className="text-[10px] text-gray-400">
                              {quotationItems.mineralWater.enabled === 'yes'
                                ? (quotationItems.mineralWater.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                                : 'Guests × Rate'}
                            </p>
                          </div>
                          <div className="col-span-1">
                            <Dropdown>
                              <DropdownTrigger>
                                <button
                                  type="button"
                                  className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                  disabled={isLockedStatus}
                                >
                                  {quotationItems.mineralWater.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.mineralWater.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                                  <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                                </button>
                              </DropdownTrigger>
                              <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                <DropdownMenu
                                  selectedKeys={new Set([quotationItems.mineralWater.enabled])}
                                  selectionMode="single"
                                  onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, enabled: Array.from(keys)[0] } }))}
                                >
                                  <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                  <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                  <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                                </DropdownMenu>
                              </DropdownPopover>
                            </Dropdown>
                          </div>
                          <div className="col-span-2">
                            {quotationItems.mineralWater.enabled === 'yes' ? (
                              quotationItems.mineralWater.inputMode === 1 ? (
                                <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                  value={quotationItems.mineralWater.lumpsum}
                                  onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, lumpsum: e.target.value } }))}
                                  disabled={isLockedStatus} />
                              ) : (
                                <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                  value={quotationItems.mineralWater.rate}
                                  onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, rate: e.target.value } }))}
                                  disabled={isLockedStatus} />
                              )
                            ) : quotationItems.mineralWater.enabled === 'complementary' ? (
                              <span className="text-[10px] text-green-600 font-semibold text-center block">4 Dispensers</span>
                            ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                          </div>
                          <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                          <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                            {quotationItems.mineralWater.enabled === 'yes'
                              ? (quotationItems.mineralWater.inputMode === 1
                                ? (Number(quotationItems.mineralWater.lumpsum) || 0).toLocaleString()
                                : ((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString())
                              : '—'}
                          </div>
                          <div className="col-span-1">
                            {quotationItems.mineralWater.enabled === 'yes' ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                                value={quotationItems.mineralWater.discount}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, discount: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                          </div>
                          <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.mwSub.toLocaleString() : '—'}</div>
                        </div>
                        {quotationItems.mineralWater.enabled === 'yes' && (
                          <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 2 } }))}
                                disabled={isLockedStatus}
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                              >
                                Per Person
                              </button>
                              <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 1 } }))}
                                disabled={isLockedStatus}
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                              >
                                Lump Sum
                              </button>
                            </div>
                            {quotationItems.mineralWater.inputMode === 1 && quotationCalc.mwPerPax !== null && (
                              <span className="text-[10px] text-purple-600 font-semibold">
                                = PKR {quotationCalc.mwPerPax % 1 === 0 ? quotationCalc.mwPerPax.toLocaleString() : quotationCalc.mwPerPax.toFixed(2)} / person
                              </span>
                            )}
                            {quotationItems.mineralWater.inputMode !== 1 && quotationItems.mineralWater.rate && quotationCalc.g > 0 && (
                              <span className="text-[10px] text-blue-500 font-medium">
                                Total = PKR {((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Pantry Charges ── */}
                      <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbPantryCharges') || 'Pantry Charges'}</p>
                          <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.pantryCharges.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.pantryCharges.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.pantryCharges.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                              value={quotationItems.pantryCharges.rate}
                              onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, rate: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.pantryCharges.enabled === 'yes' ? (Number(quotationItems.pantryCharges.rate) || 0).toLocaleString() : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.pantryCharges.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.pantryCharges.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.pantryCharges.enabled === 'yes' ? quotationCalc.pcSub.toLocaleString() : '—'}</div>
                      </div>

                      {/* ── Flower Work ── */}
                      <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbFlowerWork') || 'Flower Work'}</p>
                          <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.freshFlower.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.freshFlower.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.freshFlower.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.freshFlower.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                              value={quotationItems.freshFlower.rate}
                              onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, rate: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : quotationItems.freshFlower.enabled === 'complementary' ? (
                            <span className="text-[10px] text-green-600 font-semibold text-center block">{t('qbOnStage') || 'On Stage'}</span>
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.freshFlower.enabled === 'yes' ? (Number(quotationItems.freshFlower.rate) || 0).toLocaleString() : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.freshFlower.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.freshFlower.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.freshFlower.enabled === 'yes' ? quotationCalc.ffSub.toLocaleString() : '—'}</div>
                      </div>

                      {/* Addons */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-bold text-gray-600 uppercase">{t('qbAddons') || 'Addons'}</h4>
                          <button
                            type="button"
                            onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, addons: [...(p.addons || []), { id: Date.now(), name: '', lumpsum: '', discount: '' }] }))}
                            disabled={isLockedStatus}
                            className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                          >
                            + Add More
                          </button>
                        </div>

                        {(quotationItems.addons || []).map((addon, index) => (
                          <div key={addon.id || index} className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100 mt-2">
                            <div className="col-span-3">
                              <input type="text" className="w-full p-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:outline-none" placeholder={t('qbAddonName') || 'Addon Name'}
                                value={addon.name}
                                onChange={e => {
                                  const newAddons = [...quotationItems.addons];
                                  newAddons[index].name = e.target.value;
                                  setQuotationItems(p => ({ ...p, addons: newAddons }));
                                }}
                                disabled={isLockedStatus} />
                            </div>
                            <div className="col-span-1 flex justify-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span></div>
                            <div className="col-span-2">
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                                value={addon.lumpsum}
                                onChange={e => {
                                  const newAddons = [...quotationItems.addons];
                                  newAddons[index].lumpsum = e.target.value;
                                  setQuotationItems(p => ({ ...p, addons: newAddons }));
                                }}
                                disabled={isLockedStatus} />
                            </div>
                            <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                              {(Number(addon.lumpsum) || 0).toLocaleString()}
                            </div>
                            <div className="col-span-1">
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                                value={addon.discount}
                                onChange={e => {
                                  const newAddons = [...quotationItems.addons];
                                  newAddons[index].discount = e.target.value;
                                  setQuotationItems(p => ({ ...p, addons: newAddons }));
                                }}
                                disabled={isLockedStatus} />
                            </div>
                            <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">
                              {Math.max(0, (Number(addon.lumpsum) || 0) - (Number(addon.discount) || 0)).toLocaleString()}
                            </div>
                            <div className="col-span-1 text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  if (isLockedStatus) return;
                                  const newAddons = quotationItems.addons.filter((_, i) => i !== index);
                                  setQuotationItems(p => ({ ...p, addons: newAddons }));
                                }}
                                disabled={isLockedStatus}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Grand Total */}
                      <div className="flex items-center justify-between bg-blue-600 text-white rounded-lg px-4 py-3 mt-1">
                        <span className="text-xs font-bold uppercase tracking-widest">{t('qbGrandTotal') || 'Grand Total'}</span>
                        <span className="text-base font-bold">PKR {quotationCalc.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Final Amount + Advance — same row */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-md mb-3" dangerouslySetInnerHTML={{ __html: t('lawnRentMandatoryNote') || '<b>Note:</b> Lawn rent is mandatory for booking confirmation.' }} />
                )}
                <div className={`grid ${bookingConfirm.to === 'Site Visited' ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                  {/* Final Invoice Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('finalInvoiceAmount')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">PKR</span>
                      <input
                        type="number" onWheel={(e) => e.target.blur()}
                        className={`w-full p-2.5 pl-12 border border-gray-200 rounded-lg text-sm bg-gray-50 cursor-not-allowed`}
                        value={quotationCalc.total || bookingData.finalAmount}
                        readOnly
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Advance Received */}
                  {bookingConfirm.to !== 'Site Visited' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('advanceReceived')}</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">PKR</span>
                        <input
                          type="number" onWheel={(e) => e.target.blur()}
                          className={`w-full p-2.5 pl-12 border border-gray-200 rounded-lg text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={bookingData.advanceAmount}
                          onChange={(e) => setBookingData({ ...bookingData, advanceAmount: e.target.value })}
                          placeholder="0"
                          disabled={isLockedStatus}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {bookingConfirm.to !== 'Site Visited' && (quotationCalc.total || bookingData.finalAmount) && bookingData.advanceAmount && (
                  <p className="text-xs text-gray-500 -mt-1">
                    {t('balanceDue')} PKR {(Number(quotationCalc.total || bookingData.finalAmount) - Number(bookingData.advanceAmount)).toLocaleString()}
                  </p>
                )}

                {/* Booking Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{t('bookingNotes')}</label>
                  <textarea
                    className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm h-20 resize-none ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                    placeholder={t('bookingNotesPlaceholder')}
                    disabled={isLockedStatus}
                  />
                </div>

                {/* Summary */}
                {isBookingFormValid && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">{t('bookingSummary')}</p>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p><span className="text-blue-500">{t('venue')}:</span> {(venues && venues.length > 0 ? venues : VENUES).find(v => v.id === bookingData.venue)?.name || bookingData.venue}</p>
                      <p><span className="text-blue-500">{t('date')}:</span> {formatDate(bookingData.eventDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      <p><span className="text-blue-500">{t('amount')}:</span> PKR {Number(quotationCalc.total || bookingData.finalAmount).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-4 sm:px-5 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end rounded-b-2xl flex-shrink-0">
                <button
                  onClick={handleBookingCancel}
                  disabled={saving || isLockedStatus}
                  className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleBookingConfirm}
                  disabled={!isBookingFormValid || saving || isLockedStatus}
                  className={`px-5 py-2.5 text-sm rounded-xl font-semibold transition-colors flex items-center justify-center min-w-[140px] gap-2 ${!isBookingFormValid || saving || isLockedStatus
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                    }`}
                >
                  {saving ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <><CheckCircle className="w-4 h-4" />{bookingConfirm?.to === 'Site Visited' ? 'Submit Tentative' : t('confirmBookingTitle')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ── Stage Change Confirm ── */}
        {stageChangeConfirm && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 w-full max-w-sm animate-scale-in">
              <h3 className="text-base font-bold text-gray-900 mb-1">{t('stageChangeConfirmTitle') || 'Confirm Stage Change'}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('moveLeadFrom') || 'Move lead from '}
                <span className="font-semibold text-gray-800"> {stageChangeConfirm.from} </span>
                {t('moveLeadTo') || ' to '}
                <span className="font-semibold text-blue-600"> {stageChangeConfirm.to}</span>?
              </p>
              {stageChangeConfirm.to === 'Lost' && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t('lossReasonLabel') || 'Why was this lead lost?'} <span className="text-red-500">*</span>
                  </label>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {LOSS_REASONS.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setLossReason(r.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${lossReason === r.id
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {t('loss_' + r.id) || r.label}
                      </button>
                    ))}
                  </div>
                  {lossReason === 'postponed' && (
                    <p className="text-[11px] text-amber-600 mt-1.5 leading-snug">
                      {t('lossPostponedHint') || 'Set a follow-up date on the lead before saving — postponed events are the warmest leads you have.'}
                    </p>
                  )}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {stageChangeConfirm.to === 'Lost' && lossReason === 'other'
                    ? (t('notesRequired') || 'Notes (Required)')
                    : (t('notesOptional') || 'Notes (Optional)')}
                </label>
                <textarea
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm h-20 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={stageChangeNote}
                  onChange={(e) => setStageChangeNote(e.target.value)}
                  placeholder={stageChangeConfirm.to === 'Lost' && lossReason === 'competitor'
                    ? (t('lossCompetitorPlaceholder') || 'Which venue did they choose?')
                    : 'Add any notes about this stage change...'}
                />
              </div>
              <p className="text-xs text-gray-400 mb-4">{t('stageChangeTimelineLog') || 'This will be logged in the timeline.'}</p>
              <div className="flex gap-2 justify-end">
                <button onClick={handleStageChangeCancel}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50">
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleStageChangeConfirm}
                  disabled={stageChangeConfirm.to === 'Lost' && (!lossReason || (lossReason === 'other' && !stageChangeNote.trim()))}
                  className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center">
                  {t('confirm') || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
